import { AG_GRID_LOCALE_EN, AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { Divider, LinearProgress, Menu, MenuItem } from '@mui/material';
import { BodyScrollEndEvent, ColDef, GridApi } from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContextMenu } from '../../hooks/useContextMenu';
import { useTheme } from '../../hooks/useTheme';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getItemFromLocalStorage, removeItemFromLocalStorage, storeItemToLocalStorage } from '../../utils/localStorage';
import IconButton from '../ui/IconButton';
import TableOptionBar from './TableOptionBar';

interface GenericAgGridTableProps<T> {
    rowData: T[];
    columnDefs: ColDef<T>[];
    loading?: boolean;
    defaultColDef?: ColDef<T>;
    children?: React.ReactNode;
    rowSelectionMode?: 'multiRow' | 'singleRow';
    agGridProps?: Partial<AgGridReactProps<T>>;
    headerLeftComponent?: React.ReactNode;
    headerRightComponent?: React.ReactNode;
    exportCsvFileName?: string;
    agGridContainerStyle?: React.CSSProperties;
    storageKey?: string;
    showOptionsBar?: boolean;
    barLineColor?: string;
    showSearch?: boolean;
    gridRef?: React.LegacyRef<AgGridReact>;
    containerRef?: React.LegacyRef<HTMLDivElement>;
    title?: string;
}

const GenericAgGridTable = <T,>({
    rowData,
    columnDefs,
    loading = false,
    defaultColDef,
    children,
    rowSelectionMode,
    agGridProps,
    headerLeftComponent,
    headerRightComponent,
    exportCsvFileName,
    agGridContainerStyle,
    showOptionsBar = true,
    storageKey,
    barLineColor,
    showSearch = true,
    gridRef: ref,
    containerRef,
    title,
}: GenericAgGridTableProps<T>) => {
    const { t, i18n } = useTranslation();
    const { agGridTheme, agChartTheme, muiTheme } = useTheme();
    const internalGridRef = useRef<AgGridReact>(null);
    const gridRef = (ref || internalGridRef) as React.RefObject<AgGridReact>;
    const stored = storageKey ? JSON.parse(getItemFromLocalStorage(storageKey) || '{}') : {};
    const [quickFilter, setQuickFilter] = useState<string>(stored.quickFilter || '');
    const [initialColumnState] = useState(stored.columnState || null);
    const [initialFilterModel] = useState(stored.filterModel || null);
    const [initialColumnGroupState] = useState(stored.columnGroupState || null);
    const [isShortHeight, setIsShortHeight] = useState(window.innerHeight <= 700);
    const exportButtonRef = useRef<HTMLButtonElement>(null);

    const { anchorPosition, isOpen, handleOpen, handleClose } = useContextMenu(false);

    useEffect(() => {
        const handleResize = () => {
            setIsShortHeight(window.innerHeight <= 700);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const saveState = () => {
        const api: GridApi | undefined = gridRef.current?.api;
        if (!api || !storageKey) return;

        const state = {
            columnState: api.getColumnState(),
            filterModel: api.getFilterModel(),
            columnGroupState: api.getColumnGroupState(),
            quickFilter,
        };
        storeItemToLocalStorage(storageKey, JSON.stringify(state));
    };

    const saveScrollPositionThrottled = (event: BodyScrollEndEvent<T, any>) => {
        if (event.direction === 'horizontal') return;

        const api = gridRef.current?.api;
        if (!api) return;

        const renderedNodes = api.getRenderedNodes();
        if (!renderedNodes.length) return;
        const range = api.getVerticalPixelRange();
        const firstVisibleNode = renderedNodes.find((node) => node.rowTop! + node.rowHeight! > range.top);

        if (firstVisibleNode?.id) {
            storeItemToLocalStorage('aggrid-row-scroll-y', JSON.stringify({ rowNodeId: firstVisibleNode.id }));
        }
    };

    const restoreScrollPosition = () => {
        const api = gridRef.current?.api;
        if (!api) return;

        try {
            const saved = JSON.parse(getItemFromLocalStorage('aggrid-row-scroll-y') || '{}');
            if (saved?.rowNodeId) {
                const node = api.getRowNode(saved.rowNodeId);
                if (node) {
                    api.ensureNodeVisible(node, 'top');
                }
            }
        } catch {}
    };

    const commonEvents = {
        onFilterChanged: saveState,
        onSortChanged: saveState,
        onColumnMoved: saveState,
        onColumnVisible: saveState,
        onColumnResized: saveState,
        onColumnRowGroupChanged: saveState,
        onColumnValueChanged: saveState,
        onColumnPivotChanged: saveState,
        onDisplayedColumnsChanged: saveState,
        onNewColumnsLoaded: saveState,
        onBodyScrollEnd: (event: BodyScrollEndEvent<T, any>) => saveScrollPositionThrottled(event),
    };

    const aggFuncs = useMemo(() => {
        return {
            countAllItems: (params: any) => {
                const values: string[] = params.values;
                if (!values || values.length === 0) return '';

                const counts: Record<string, number> = {};
                values.forEach((val) => {
                    counts[val] = (counts[val] || 0) + 1;
                });

                const parts = Object.entries(counts).map(([item, count]) => `${count} ${item}`);

                return parts.join(', ');
            },
        };
    }, []);

    const onExportCsv = () => {
        gridRef.current!.api.exportDataAsCsv({
            fileName: exportCsvFileName,
            columnSeparator: ';',
            allColumns: true,
        });
        handleClose();
    };

    const onExportExcel = () => {
        gridRef.current!.api.exportDataAsExcel({
            fileName: (exportCsvFileName || 'export') + '.xlsx',
            allColumns: true,
        });
        handleClose();
    };

    const onResetState = () => {
        if (storageKey) {
            const api: GridApi | undefined = gridRef.current?.api;
            removeItemFromLocalStorage(storageKey);
            api?.setFilterModel(null);
            api?.resetColumnState();
            api?.resetColumnGroupState();
            setQuickFilter('');
            api?.refreshCells();
        }
    };

    const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        handleOpen(event);
    };

    return (
        <div className="fx-c fx-fg-1">
            {showOptionsBar && (
                <TableOptionBar
                    onSearchChange={(text) => {
                        setQuickFilter(text);
                        saveState();
                    }}
                    searchValue={quickFilter}
                    leftComponent={headerLeftComponent}
                    style={{ borderColor: barLineColor || muiTheme.palette.divider }}
                    showSearch={showSearch}
                    title={title}
                >
                    {headerRightComponent}
                    {(!!exportCsvFileName?.length || !!storageKey?.length) && <Divider orientation="vertical" flexItem />}
                    {!!storageKey?.length && (
                        <IconButton iconName="restartAlt" onClick={onResetState} title={capitalizeFirstLetter(t('resetTable'))} color="warning" />
                    )}

                    {!!exportCsvFileName?.length && (
                        <>
                            <IconButton
                                ref={exportButtonRef}
                                iconName="fileDownload"
                                onClick={handleExportClick}
                                title={capitalizeFirstLetter(t('export'))}
                            />
                            <Menu open={isOpen} onClose={handleClose} anchorReference="anchorPosition" anchorPosition={anchorPosition || undefined}>
                                <MenuItem onClick={onExportCsv}>{t('csv').toUpperCase()}</MenuItem>
                                <MenuItem onClick={onExportExcel}>{capitalizeFirstLetter(t('excel'))}</MenuItem>
                            </Menu>
                        </>
                    )}
                </TableOptionBar>
            )}
            <div style={{ flex: 1, ...agGridContainerStyle }} ref={containerRef}>
                <AgGridReact<T>
                    key={storageKey + muiTheme.palette.mode + i18n.language}
                    ref={gridRef}
                    theme={agGridTheme}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    quickFilterText={quickFilter}
                    loading={loading}
                    groupDisplayType={'singleColumn'}
                    overlayLoadingTemplate={`${capitalizeFirstLetter(t('loading'))}...`}
                    loadingOverlayComponent={() => (
                        <div className="ag-overlay-loading-center" role="presentation">
                            <div role="presentation" className="custom-loading-overlay w-200px">
                                <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: 'white' } }} />
                            </div>
                        </div>
                    )}
                    localeText={i18n.language === 'fr' ? AG_GRID_LOCALE_FR : AG_GRID_LOCALE_EN}
                    multiSortKey="ctrl"
                    domLayout={isShortHeight ? 'autoHeight' : 'normal'}
                    rowDragManaged
                    suppressCellFocus
                    enableCellTextSelection
                    sideBar={{
                        toolPanels: [
                            {
                                id: 'columns',
                                labelDefault: 'Colonnes',
                                labelKey: 'columns',
                                iconKey: 'columns',
                                toolPanel: 'agColumnsToolPanel',
                                toolPanelParams: { suppressPivots: true, suppressPivotMode: true },
                            },
                            {
                                id: 'filters',
                                labelDefault: 'Filtres',
                                labelKey: 'filters',
                                iconKey: 'filter',
                                toolPanel: 'agFiltersToolPanel',
                            },
                        ],
                        defaultToolPanel: '',
                    }}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                        autoHeight: true,
                        minWidth: 100,
                        initialWidth: 200,
                        headerStyle: { textTransform: 'uppercase' },
                        enableValue: true,
                        enableRowGroup: true,
                        ...defaultColDef,
                    }}
                    rowSelection={rowSelectionMode ? { mode: rowSelectionMode } : undefined}
                    enableRowPinning
                    enableCharts
                    chartThemes={['myCustomChartTheme']}
                    customChartThemes={{
                        myCustomChartTheme: agChartTheme,
                    }}
                    cellSelection={false}
                    detailRowHeight={500}
                    onGridReady={(params) => {
                        const api = params.api;
                        if (initialColumnState) {
                            api.applyColumnState({ state: initialColumnState, applyOrder: true });
                        }
                        if (initialFilterModel) {
                            api.setFilterModel(initialFilterModel);
                        }
                        if (initialColumnGroupState) {
                            api.setColumnGroupState(initialColumnGroupState);
                        }
                    }}
                    onFirstDataRendered={restoreScrollPosition}
                    rowClass={'fx-r'}
                    maintainColumnOrder
                    suppressScrollOnNewData
                    suppressAggFuncInHeader
                    stopEditingWhenCellsLoseFocus
                    aggFuncs={aggFuncs}
                    selectionColumnDef={{ pinned: 'left' }}
                    enableCellSpan
                    {...commonEvents}
                    {...agGridProps}
                />
            </div>
            {children}
        </div>
    );
};

export default GenericAgGridTable;
